import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Check, X, Minus, Mail, Calendar } from "lucide-react";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import SEO from "@/components/marketing/SEO";
import DemoRequestDialog from "@/components/marketing/DemoRequestDialog";

export interface CompetitorData {
  name: string;
  slug: string;
  type: string;
  hasCommissionPerCover: boolean | "variable";
  hasPriceCeiling: boolean | "variable";
  complexity: "simple" | "moyenne" | "élevée";
  targetAudience: string;
  focus: string;
  supportLanguage: string;
  whenToChoose: string[];
  pricingDescription: string;
  conclusionStrength: string;
  startingPriceChf?: number;
  commissionPerCoverChf?: string;
  freeTrialDays?: number | string;
  countryOfOrigin?: string;
  websiteUrl?: string;
  // Optional overrides for SEO. Quand fournis, remplacent le title/description
  // génériques. Utiliser pour les pages à fort volume d'impressions où on veut
  // optimiser le CTR (vu dans GSC : impressions hautes mais CTR <1% = title peu accrocheur).
  seoTitle?: string;
  seoDescription?: string;
}

interface ComparisonPageProps {
  competitor: CompetitorData;
}

const PIKTABLE_FACTS = {
  startingPriceChf: 49,
  cheapestPriceChf: 29,
  commission: "0 CHF",
  freeTrialDays: 30,
  longTrialDays: 60,
  country: "Suisse (Lausanne)",
  website: "https://piktable.ch",
};

const ComparisonPage = ({ competitor }: ComparisonPageProps) => {
  const navigate = useNavigate();

  const renderBoolean = (value: boolean | "variable", inverse = false) => {
    if (value === "variable") return <Minus className="w-5 h-5 text-muted-foreground" />;
    const show = inverse ? !value : value;
    return show ? (
      <Check className="w-5 h-5 text-green-500" />
    ) : (
      <X className="w-5 h-5 text-red-500" />
    );
  };

  const today = new Date().toISOString().split("T")[0];

  const commissionLabel =
    competitor.commissionPerCoverChf
      ?? (competitor.hasCommissionPerCover === true
        ? "Oui"
        : competitor.hasCommissionPerCover === false
          ? "Non"
          : "Variable");

  const competitorPriceLabel =
    competitor.startingPriceChf != null
      ? `À partir d'environ ${competitor.startingPriceChf} CHF/mois (source publique du fournisseur)`
      : "Tarif sur devis auprès du fournisseur";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://piktable.ch/" },
        { "@type": "ListItem", "position": 2, "name": "Comparatifs", "item": "https://piktable.ch/comparatif" },
        { "@type": "ListItem", "position": 3, "name": `Piktable vs ${competitor.name}`, "item": `https://piktable.ch/comparatif/piktable-vs-${competitor.slug}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": `Piktable vs ${competitor.name} : quelle solution choisir pour votre restaurant ?`,
      "description": `Comparatif complet entre Piktable et ${competitor.name} pour la gestion de réservations de restaurants en Suisse.`,
      "author": { "@type": "Organization", "name": "Piktable" },
      "publisher": {
        "@type": "Organization",
        "name": "Piktable",
        "logo": { "@type": "ImageObject", "url": "https://piktable.ch/favicon.png" },
      },
      "datePublished": "2025-11-15",
      "dateModified": today,
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Piktable",
      "description": "Logiciel SaaS de gestion de réservations pour restaurants suisses, sans commission par couvert.",
      "brand": { "@type": "Brand", "name": "Piktable" },
      "category": "Restaurant booking software",
      "offers": [
        {
          "@type": "Offer",
          "name": "Offre Standard",
          "price": PIKTABLE_FACTS.startingPriceChf,
          "priceCurrency": "CHF",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": PIKTABLE_FACTS.startingPriceChf,
            "priceCurrency": "CHF",
            "unitText": "MONTH",
          },
          "availability": "https://schema.org/InStock",
          "url": "https://piktable.ch/pricing",
        },
        {
          "@type": "Offer",
          "name": "Offre de Printemps 2026 (50 premiers restaurants)",
          "price": PIKTABLE_FACTS.cheapestPriceChf,
          "priceCurrency": "CHF",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": PIKTABLE_FACTS.cheapestPriceChf,
            "priceCurrency": "CHF",
            "unitText": "MONTH",
          },
          "availability": "https://schema.org/LimitedAvailability",
          "url": "https://piktable.ch/pricing",
        },
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "3",
        "bestRating": "5",
        "worstRating": "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": competitor.name,
      "description": competitor.pricingDescription,
      "brand": { "@type": "Brand", "name": competitor.name },
      "category": "Restaurant booking software",
      ...(competitor.websiteUrl && { "url": competitor.websiteUrl }),
      ...(competitor.startingPriceChf != null && {
        "offers": {
          "@type": "Offer",
          "price": competitor.startingPriceChf,
          "priceCurrency": "CHF",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": competitor.startingPriceChf,
            "priceCurrency": "CHF",
            "unitText": "MONTH",
          },
        },
      }),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": `Quelle est la différence entre Piktable et ${competitor.name} ?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Piktable est un logiciel de réservation suisse à 49 CHF/mois (ou 29 CHF à vie pour les 50 fondateurs du Printemps 2026), sans commission par couvert. ${competitor.name} est ${competitor.type.toLowerCase()} avec ${competitor.hasCommissionPerCover ? "des commissions par couvert" : "un modèle d'abonnement mensuel"}.`,
          },
        },
        {
          "@type": "Question",
          "name": `${competitor.name} ou Piktable : lequel est moins cher ?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Piktable coûte 49 CHF/mois en Standard (ou 29 CHF/mois à vie pour les 50 premiers fondateurs du Printemps 2026), sans commission par couvert. ${competitor.name} : ${competitorPriceLabel}. Pour une comparaison précise, consulte directement le site officiel de ${competitor.name}. Piktable reste généralement plus économique et plus prévisible pour un restaurant suisse moyen.`,
          },
        },
        {
          "@type": "Question",
          "name": `Puis-je essayer Piktable et ${competitor.name} gratuitement ?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Piktable offre 30 jours gratuits sur l'offre Standard et 60 jours pour les offres Printemps 2026 et École Hôtelière, sans carte bancaire. Pour ${competitor.name}, les conditions d'essai varient : consulte directement leur site officiel ou demande une démo.`,
          },
        },
        {
          "@type": "Question",
          "name": `Y a-t-il une commission par couvert chez ${competitor.name} ?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${competitor.name} : ${commissionLabel}. Piktable : aucune commission par couvert, jamais — c'est l'une de nos quatre garanties officielles.`,
          },
        },
        {
          "@type": "Question",
          "name": `${competitor.name} est-il disponible en Suisse romande avec support en français ?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `${competitor.name} : ${competitor.supportLanguage}, origine ${competitor.countryOfOrigin ?? "non-suisse"}. Piktable est conçu à Lausanne, avec un support 100 % en français, dans le fuseau horaire suisse.`,
          },
        },
        {
          "@type": "Question",
          "name": `Comment migrer de ${competitor.name} vers Piktable ?`,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": `Piktable propose un accompagnement optionnel à 100 CHF (remboursable) qui inclut la migration de tes données existantes, la configuration du plan de salle, des horaires et du widget. Tu peux aussi tout configurer par toi-même en moins de 30 minutes.`,
          },
        },
      ],
    },
  ];

  return (
    <>
      <SEO
        title={competitor.seoTitle ?? `${competitor.name} vs Piktable : tarifs, commissions et alternatives en Suisse`}
        description={competitor.seoDescription ?? `${competitor.name} ou Piktable pour gérer les réservations de ton restaurant suisse ? On compare prix, commission par couvert, fonctionnalités et support — verdict honnête pour choisir.`}
        canonical={`/comparatif/piktable-vs-${competitor.slug}`}
        structuredData={structuredData}
      />

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 text-center">
              Piktable vs {competitor.name} : quelle solution choisir pour ton restaurant en Suisse ?
            </h1>

            <p className="text-lg text-muted-foreground text-center mb-10 max-w-2xl mx-auto">
              Comparatif objectif pour t'aider à choisir la meilleure solution de réservation pour ton établissement.
            </p>

            <section className="mb-12">
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
                Les différences en 10 secondes
              </h2>
              <div className="bg-card rounded-lg p-6 md:p-8 border border-border">
                <dl className="grid md:grid-cols-2 gap-6">
                  <div>
                    <dt className="text-sm text-muted-foreground">Piktable</dt>
                    <dd className="font-semibold text-foreground text-lg mt-1">
                      {PIKTABLE_FACTS.startingPriceChf} CHF/mois (ou {PIKTABLE_FACTS.cheapestPriceChf} CHF/mois à vie pour les 50 fondateurs du Printemps 2026)
                    </dd>
                    <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <li>· 0 CHF de commission par couvert, à vie</li>
                      <li>· 30 à 60 jours gratuits selon l'offre</li>
                      <li>· Conçu à Lausanne, support 100 % en français</li>
                      <li>· Facturation annuelle, résiliable à tout moment avec remboursement au prorata</li>
                    </ul>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground">{competitor.name}</dt>
                    <dd className="font-semibold text-foreground text-lg mt-1">
                      {competitorPriceLabel}
                    </dd>
                    <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
                      <li>· Commission par couvert : {commissionLabel}</li>
                      {competitor.freeTrialDays != null && (
                        <li>
                          · Essai gratuit :{" "}
                          {typeof competitor.freeTrialDays === "number"
                            ? `${competitor.freeTrialDays} jours`
                            : competitor.freeTrialDays}
                        </li>
                      )}
                      {competitor.countryOfOrigin && (
                        <li>· Origine : {competitor.countryOfOrigin}</li>
                      )}
                      <li>· Support : {competitor.supportLanguage}</li>
                    </ul>
                  </div>
                </dl>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
                Comparaison détaillée
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-card rounded-lg overflow-hidden">
                  <thead>
                    <tr className="bg-primary/10">
                      <th className="text-left p-4 font-semibold text-foreground">Critère</th>
                      <th className="text-center p-4 font-semibold text-primary">Piktable</th>
                      <th className="text-center p-4 font-semibold text-foreground">{competitor.name}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr>
                      <td className="p-4 text-foreground">Prix de départ</td>
                      <td className="p-4 text-center font-medium text-primary">
                        {PIKTABLE_FACTS.startingPriceChf} CHF/mois
                      </td>
                      <td className="p-4 text-center text-muted-foreground">
                        {competitorPriceLabel}
                      </td>
                    </tr>
                    <tr className="bg-muted/30">
                      <td className="p-4 text-foreground">Commission par couvert</td>
                      <td className="p-4 text-center text-green-600 font-medium">0 CHF</td>
                      <td className="p-4 text-center text-muted-foreground">
                        {commissionLabel}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-4 text-foreground">Essai gratuit</td>
                      <td className="p-4 text-center text-muted-foreground">
                        30 à 60 jours (selon l'offre)
                      </td>
                      <td className="p-4 text-center text-muted-foreground">
                        {competitor.freeTrialDays != null
                          ? typeof competitor.freeTrialDays === "number"
                            ? `${competitor.freeTrialDays} jours`
                            : competitor.freeTrialDays
                          : "Sur demande"}
                      </td>
                    </tr>
                    <tr className="bg-muted/30">
                      <td className="p-4 text-foreground">Pays d'origine</td>
                      <td className="p-4 text-center text-muted-foreground">
                        Suisse (Lausanne)
                      </td>
                      <td className="p-4 text-center text-muted-foreground">
                        {competitor.countryOfOrigin ?? "—"}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-4 text-foreground">Type de solution</td>
                      <td className="p-4 text-center text-muted-foreground">
                        Outil de gestion interne
                      </td>
                      <td className="p-4 text-center text-muted-foreground">
                        {competitor.type}
                      </td>
                    </tr>
                    <tr className="bg-muted/30">
                      <td className="p-4 text-foreground">Plafond tarifaire garanti</td>
                      <td className="p-4 text-center flex justify-center">{renderBoolean(true)}</td>
                      <td className="p-4 text-center">
                        <div className="flex justify-center">
                          {renderBoolean(competitor.hasPriceCeiling)}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className="p-4 text-foreground">Complexité de mise en place</td>
                      <td className="p-4 text-center text-muted-foreground">Simple (10 min)</td>
                      <td className="p-4 text-center text-muted-foreground capitalize">
                        {competitor.complexity}
                      </td>
                    </tr>
                    <tr className="bg-muted/30">
                      <td className="p-4 text-foreground">Focalisation</td>
                      <td className="p-4 text-center text-muted-foreground">Gestion interne</td>
                      <td className="p-4 text-center text-muted-foreground">
                        {competitor.focus}
                      </td>
                    </tr>
                    <tr>
                      <td className="p-4 text-foreground">Support & langue</td>
                      <td className="p-4 text-center text-muted-foreground">
                        Français, Lausanne
                      </td>
                      <td className="p-4 text-center text-muted-foreground">
                        {competitor.supportLanguage}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
                Quand choisir {competitor.name} ?
              </h2>

              <div className="bg-card rounded-lg p-6 border border-border">
                <p className="text-muted-foreground mb-4">
                  {competitor.name} est une solution qui peut convenir si tu recherches {competitor.conclusionStrength}.
                  C'est particulièrement adapté pour certains types d'établissements avec des besoins spécifiques.
                </p>

                <p className="text-foreground font-medium mb-3">
                  Tu pourrais préférer {competitor.name} si :
                </p>

                <ul className="space-y-2">
                  {competitor.whenToChoose.map((reason, index) => (
                    <li key={index} className="flex items-start gap-2 text-muted-foreground">
                      <span className="text-primary mt-1">•</span>
                      {reason}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
                Quand Piktable est-il un meilleur choix ?
              </h2>

              <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                <p className="text-muted-foreground mb-4">
                  Piktable a été conçu à Lausanne spécifiquement pour les cafés, bistrots et petits restaurants
                  indépendants suisses. Si tu cherches une solution simple, sans commission sur les couverts et avec
                  une tarification transparente, Piktable est probablement plus adapté à tes besoins.
                </p>

                <p className="text-muted-foreground mb-6">
                  Contrairement aux grandes plateformes internationales, Piktable se concentre sur l'essentiel :
                  t'aider à gérer tes réservations au quotidien. Le support est en français, basé à Lausanne, et
                  comprend les réalités du marché local.
                </p>

                <p className="text-foreground font-medium mb-3">
                  Piktable est pour toi si :
                </p>

                <ul className="grid md:grid-cols-2 gap-3">
                  {[
                    "Tu gères un café, bistrot ou restaurant suisse",
                    "Tu reçois encore beaucoup de réservations par téléphone",
                    "Tu n'as pas de responsable IT et cherches un outil prêt à l'emploi",
                    "Tu veux éviter les commissions par couvert qui grignotent tes marges",
                    "Tu préfères un tarif fixe et prévisible (49 CHF/mois, sans commission)",
                    "Tu valorises un support local en français",
                    "Tu souhaites être opérationnel en moins de 30 minutes",
                    "Tu veux garder le contrôle total sur tes données clients",
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-muted-foreground">
                      <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
                Combien ça coûte ?
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-primary/5 rounded-lg p-6 border border-primary/20">
                  <h3 className="font-playfair text-xl font-bold text-primary mb-4">Piktable</h3>
                  <p className="text-muted-foreground mb-4">Tarification simple et transparente :</p>
                  <ul className="space-y-2 text-muted-foreground text-sm">
                    <li><strong>Offre Standard</strong> : 49 CHF/mois tout inclus, sans commission</li>
                    <li><strong>Offre Printemps 2026</strong> : 29 CHF/mois à vie (50 premiers restaurants, jusqu'au 30 septembre 2026)</li>
                    <li><strong>Offre École Hôtelière</strong> : 29 CHF/mois à vie (diplômés &lt; 3 ans, resto &lt; 18 mois)</li>
                    <li><strong>Parrainage</strong> : 49 → 39 → 34 → 29 CHF/mois à vie</li>
                  </ul>
                  <p className="mt-4 text-sm text-primary font-medium">
                    ✓ Zéro commission par couvert<br />
                    ✓ Jusqu'à 60 jours gratuits<br />
                    ✓ Facturation annuelle, résiliable, remboursement prorata
                  </p>
                </div>

                <div className="bg-card rounded-lg p-6 border border-border">
                  <h3 className="font-playfair text-xl font-bold text-foreground mb-4">{competitor.name}</h3>
                  <p className="text-muted-foreground">{competitor.pricingDescription}</p>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-6">
                Conclusion : quelle solution pour ton restaurant ?
              </h2>

              <div className="prose prose-lg max-w-none text-muted-foreground">
                <p>
                  {competitor.name} reste une option pertinente pour {competitor.conclusionStrength}.
                  Si tu as des besoins spécifiques en termes de visibilité ou de fonctionnalités avancées,
                  cette solution pourrait te convenir.
                </p>

                <p>
                  En revanche, si tu gères un café, bistrot ou petit restaurant indépendant en Suisse
                  et que tu cherches avant tout un outil simple pour organiser tes réservations au quotidien,
                  Piktable est probablement la solution la plus adaptée. Pas de commission par couvert, une
                  tarification fixe et claire, et un support local qui comprend tes enjeux.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <div className="bg-card rounded-2xl p-6 md:p-8 border border-border flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <img
                    src="/images/team/gueorgui-petrov.jpg"
                    alt="Gueorgui Petrov - Responsable Produit & Croissance chez Piktable"
                    className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-primary/20"
                  />
                </div>
                <div className="text-center md:text-left">
                  <h3 className="font-playfair text-xl font-bold text-foreground mb-1">
                    Gueorgui Petrov
                  </h3>
                  <p className="text-primary font-medium mb-2">
                    Responsable Produit & Croissance
                  </p>
                  <p className="text-muted-foreground mb-3">
                    Des questions ? Je suis à ta disposition pour discuter de tes besoins
                    et t'accompagner dans ton choix.
                  </p>
                  <a
                    href="mailto:gueorgui@piktable.ch"
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    gueorgui@piktable.ch
                  </a>
                  <span className="mx-2 text-muted-foreground">|</span>
                  <a
                    href="https://calendly.com/maxdmnt/piktable"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                  >
                    <Calendar className="w-4 h-4" />
                    Réserver un appel
                  </a>
                </div>
              </div>
            </section>

            <section className="mb-8 text-xs text-muted-foreground italic text-center">
              <p>
                Informations collectées à partir des sites officiels des fournisseurs et de sources publiques.
                Les tarifs exacts de {competitor.name} peuvent varier selon la formule, la région ou la période — nous t'invitons à contacter directement {competitor.websiteUrl ? (
                  <a href={competitor.websiteUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">{competitor.name}</a>
                ) : competitor.name} pour une offre personnalisée et actualisée.
              </p>
            </section>

            <section className="text-center bg-gradient-to-r from-primary/10 to-primary/5 rounded-2xl p-8 md:p-12">
              <h3 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-4">
                Prêt à essayer Piktable ?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                Teste Piktable gratuitement pendant 30 jours, sans carte bancaire et sans engagement.
                Jusqu'à 60 jours gratuits avec l'offre Printemps 2026.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <DemoRequestDialog
                  trigger={
                    <Button size="lg" className="text-lg px-8">
                      Commencer l'essai gratuit
                    </Button>
                  }
                  context="cta"
                />
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate("/pricing")}
                  className="text-lg px-8"
                >
                  Voir les tarifs
                </Button>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ComparisonPage;
